import type { TileConfigPrice } from './useTileConfigPrices';

import { useTileConfigPrices } from './useTileConfigPrices';
import { useDisplayLowestPrice } from '../../hooks/useDisplayLowestPrice';
import { useTileContext } from '../TileContext';
import { PageTypes } from '~/utilities/constants';
import {
  INTERMEDIARY_PRICE_VARIABLE,
  PRODUCT_COLOR_VARIABLE,
  PRODUCT_NAME_VARIABLE,
  PRODUCT_SIGNINGS_VARIABLE,
  PRICE_VARIABLE,
  ORIGINAL_PRICE_VARIABLE,
  SALE_PRICE_VARIABLE,
  LOWEST_PRICE_VARIABLE,
} from '~/utilities/constants/amplienceVariables';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Signing } from '~/utilities/graphql/codegen';
import { LowestPriceDisplay } from '~/utilities/graphql/codegen';
import { localisedData } from '~/utilities/helpers';
import { useMembersOnlyPrices } from '~/utilities/hooks/useMembersOnlyPrices';
import { useMembersOnlySignings } from '~/utilities/hooks/useMembersOnlySignings';

type UseProductTileAccessibilityReturn = {
  tileAriaLabel: string;
};

const DEFAULT_TEMPLATE =
  '[product name], [product signings], [product color], [original price], [intermediary price], [sale price], [price], [lowest price 30 days]';

export function useProductTileAccessibility(): UseProductTileAccessibilityReturn {
  const { productPrice, product, tileType } = useTileContext();
  const {
    accessibilityLabels,
    priceFormatter,
    configuration: { lowestPriceDisplay },
  } = useStaticContext();
  const { lang, pageType } = useAppContext();

  const {
    priceValue,
    name,
    colour,
    exactColorDescription,
    fromPriceValue,
    intermediaryPriceValue,
    lowestPrice,
    multiSigningJson,
    personalisedPriceValue,
    priceDisplayMode,
  } = product;

  const {
    originalPrice = 'Price:',
    salePrice = 'Sale price:',
    intermediaryPriceText = 'Intermediary price:',
    lowestPrice30days = 'Lowest price 30 days:',
  } = productPrice || {};

  const { showOriginalPrice, renderLowestPrice } = useDisplayLowestPrice(
    priceValue,
    fromPriceValue
  );
  const { signings: membersOnlySignings } = useMembersOnlySignings({
    signings: multiSigningJson as Signing[],
    personalisedPrice: personalisedPriceValue,
  });

  // Same members-aware base price as the visible tile price block (TilePriceConfig)
  const { priceValue: displayedPriceValue } = useMembersOnlyPrices({
    price: priceValue,
    personalisedPrice: personalisedPriceValue,
  });
  const configPrices = useTileConfigPrices({
    priceDisplayMode,
    priceValue: displayedPriceValue,
    fromPriceValue,
    intermediaryPriceValue,
    lowestPrice,
    hideIntermediaryPrice: tileType === 'ctl',
  });

  // In CONFIG mode, announce the same prices the visible tile price block (TilePriceConfig) shows
  const isConfigPriceDisplay = lowestPriceDisplay === LowestPriceDisplay.Config;
  const hasConfigPrice = (type: TileConfigPrice['type']) =>
    configPrices.some(configPrice => configPrice.type === type);

  const announceOriginalPrice = isConfigPriceDisplay ? hasConfigPrice('rrp') : showOriginalPrice;
  const announceIntermediaryPrice = isConfigPriceDisplay
    ? hasConfigPrice('itp')
    : intermediaryPriceValue > 0 && intermediaryPriceValue !== priceValue;
  const announceLowestPrice = isConfigPriceDisplay ? hasConfigPrice('l30d') : renderLowestPrice;

  const isDiscounted = product.priceValue !== product.fromPriceValue;
  const isOnPdp = pageType === PageTypes.PRODUCT;

  const signings = (membersOnlySignings ?? []).reduce((str, signing) => {
    if (isOnPdp ? signing.showOnPdp : signing.showOnPlp) {
      return `${str}, ${signing.label}`;
    }

    return str;
  }, '');

  const colorSource = colour ?? exactColorDescription;

  const replacements: Record<string, string> = {
    [PRODUCT_NAME_VARIABLE]: name ? localisedData(product, name, lang) : '',
    [PRODUCT_SIGNINGS_VARIABLE]: signings,
    [PRODUCT_COLOR_VARIABLE]: colorSource ? localisedData(product, colorSource, lang).trim() : '',
    [PRICE_VARIABLE]: isDiscounted ? '' : priceFormatter(priceValue),
    [ORIGINAL_PRICE_VARIABLE]: announceOriginalPrice
      ? `${originalPrice} ${priceFormatter(fromPriceValue)}`.trim()
      : '',
    [SALE_PRICE_VARIABLE]: isDiscounted ? `${salePrice} ${priceFormatter(priceValue)}`.trim() : '',
    [INTERMEDIARY_PRICE_VARIABLE]: announceIntermediaryPrice
      ? `${intermediaryPriceText} ${priceFormatter(intermediaryPriceValue)}`.trim()
      : '',
    [LOWEST_PRICE_VARIABLE]:
      announceLowestPrice && lowestPrice
        ? `${lowestPrice30days} ${priceFormatter(lowestPrice)}`.trim()
        : '',
  };

  const pattern = new RegExp(
    Object.keys(replacements)
      .map(k => k.replace(/[[\]]/g, '\\$&'))
      .join('|'),
    'g'
  );

  const template = accessibilityLabels?.product?.productTileAccessibilityLabel || DEFAULT_TEMPLATE;

  const tileAriaLabel = template
    .replace(pattern, match => replacements[match] ?? '')
    .replace(/,\s*(?:,\s*)+/g, ', ')
    .replace(/^[,\s]+/, '')
    .replace(/,{0,10}$/, '')
    .trim();

  return {
    tileAriaLabel,
  };
}
